import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { catchError, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { Location } from '@angular/common';

import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { ProfilePicture } from 'src/app/shared/models/profile-picture.model';
import { Store } from '@ngxs/store';
import { RegistrationState, DriverStateModel } from 'src/app/registration/state/registration.state';
import { environment } from 'src/environments/environment.dev';
import { tap, onErrorResumeNext } from 'rxjs/operators';
import { ErrorModel } from 'src/app/shared/models/error.model';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { ProfileService } from '../../profile/profile.service';
import { ActivatedRoute } from '@angular/router';
import { of } from 'rxjs';
import { base64ToFile } from 'src/app/shared/utils';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

const ACTION_SAVE_PROFILE = 'Save profile';
const DEFAULT_PROFILE_IMAGE = '/assets/images/default-profile-picture.png';
@Component({
    selector: 'app-profile-picture',
    templateUrl: './profile-picture.component.html',
    styleUrls: ['./profile-picture.component.scss'],
    standalone: false
})
export class ProfilePictureComponent implements OnInit, OnDestroy {
  showMenu = false;
  imageChangedEvent: any;
  profilePicture: any;
  loading = false;
  menuOptionSelected = false;
  errorMessage: string;
  personalInfoCompleted = false;
  driver: any;
  acceptTypes = ['image/png', 'image/jpg', 'image/jpeg'];
  canvasRotation = 0;
  transform: ImageTransform = {};
  @ViewChild('previewImage') previewImage: ImageCropperComponent;
  private subs = new SubSink();
  private profilePicturePath: ProfilePicture = new ProfilePicture();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location,
    private readonly registrationService: RegistrationService,
    private readonly driverApiService: DriverApiService,
    private readonly profileService: ProfileService,
    private readonly timeService: DateTimeService,
    private readonly store: Store,
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer
  ) {
    this.loading = false;
    this.errorMessage = undefined;
  }

  ngOnInit(): void {
    this.subs.add(
      this.registrationService
        .loadDriver()
        .pipe(
          onErrorResumeNext(this.registrationService.loadProfilePicture()),
          tap(() => {
            this.profilePicturePath = this.store.selectSnapshot(RegistrationState.profilePicture);
          }),
          tap(() => (this.driver = this.store.selectSnapshot(RegistrationState.driver))),
          tap(() => (this.personalInfoCompleted = this.isPersonalInfoCompleted(this.driver)))
        )
        .subscribe(() => {
          if (this.profilePicturePath && this.profilePicturePath.fileUrl) {
            // Fetch the image as blob to bypass CORS issues
            this.http.get(this.profilePicturePath.fileUrl, { responseType: 'blob' }).pipe(
              catchError((error: any) => {
                this.profilePicture = DEFAULT_PROFILE_IMAGE;
                return of(null);
              })
            ).subscribe((blob: Blob | null) => {
              if (blob) {
                const objectURL = URL.createObjectURL(blob);
                this.profilePicture = this.sanitizer.bypassSecurityTrustUrl(objectURL);
              }
            });
          } else {
            this.profilePicture = DEFAULT_PROFILE_IMAGE;
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onFileInputClicked(fileInput: any) {
    fileInput.click();
  }
  isPersonalInfoCompleted(driver: DriverStateModel): boolean {
    return this.profileService.isPersonalInfoCompleted(driver);
  }
  onNavigateToProfile(): void {
    this.registrationService.navigateToProfile();
  }
  goBack(): void {
    this.location.back();
  }
  onShowMenu() {
    this.showMenu = true;
  }

  onHideMenu() {
    this.showMenu = false;
  }

  onProcessFile(event: any) {
    if (event.target.files && event.target.files.length) {
      const reader = new FileReader();
      const [file] = event.target.files;
      reader.readAsDataURL(file);

      reader.onload = () => {
        this.profilePicture = file;
      };

      this.loading = true;
      this.menuOptionSelected = true;
      this.onHideMenu();
      this.imageChangedEvent = event;
    }
  }

  onCancelCrop() {
    this.imageChangedEvent = undefined;
    this.profilePicture = undefined;
    this.menuOptionSelected = false;
  }

  onFinishCrop() {
    // Force the image cropper to emit the final cropped/rotated image
    if (this.previewImage) {
      const croppedImage = this.previewImage.crop();
      
      if (croppedImage && (croppedImage.base64 || croppedImage.blob)) {
        const fileName = this.profilePicture?.name || 'profile-picture.png';
        const fileType = this.profilePicture?.type || 'image/png';
        
        if (croppedImage.base64) {
          const imageFile = new File([base64ToFile(croppedImage.base64)], fileName, {
            type: fileType
          });
          this.profilePicture = imageFile;
        } else if (croppedImage.blob) {
          const imageFile = new File([croppedImage.blob], fileName, {
            type: fileType
          });
          this.profilePicture = imageFile;
        }
      }
    }

    if (this.profilePicture && this.profilePicture.name) {
      const extension = this.profilePicture.name
        .split('.')
        .pop()
        .toLowerCase();
      this.loading = true;
      this.subs.add(
        this.registrationService
          .saveProfilePicture(this.profilePicture, extension)
          .pipe(
            catchError((error: any) => {
              this.errorMessage = environment.errorMessage;
              this.loading = false;
              this.imageChangedEvent = undefined;
              this.menuOptionSelected = false;

              return of(false);
            })
          )
          .subscribe(() => {
            this.errorMessage = undefined;
            this.loading = false;
            this.imageChangedEvent = undefined;
            this.menuOptionSelected = false;
            this.onNavigateToProfile();
          })
      );
    }
  }

  onImageCropped(event: ImageCroppedEvent) {
    if (event && (event.base64 || event.blob)) {
      // Ensure we have a valid filename and type
      const fileName = this.profilePicture?.name || 'profile-picture.png';
      const fileType = this.profilePicture?.type || 'image/png';
      
      if (event.base64) {
        const imageFile = new File([base64ToFile(event.base64)], fileName, {
          type: fileType
        });
        this.profilePicture = imageFile;
      } else if (event.blob) {
        const imageFile = new File([event.blob], fileName, {
          type: fileType
        });
        this.profilePicture = imageFile;
      }
    }
  }

  onImageLoaded() {
    this.loading = false;
  }

  onCropperReady() {
    this.loading = false;
  }

  rotateLeft() {
    this.canvasRotation--;
    this.flipAfterRotate();
    
    // Force the image cropper to recalculate and emit the rotated image
    setTimeout(() => {
      if (this.previewImage) {
        this.previewImage.crop();
      }
    }, 100);
  }
  rotateRight() {
    this.canvasRotation++;
    this.flipAfterRotate();
    
    // Force the image cropper to recalculate and emit the rotated image
    setTimeout(() => {
      if (this.previewImage) {
        this.previewImage.crop();
      }
    }, 100);
  }

  private flipAfterRotate() {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH
    };
  }

  onLoadImageFailed() {}

  onImageError(event: any): void {
    // Fallback to default image
    this.profilePicture = DEFAULT_PROFILE_IMAGE;
  }

  onImageLoad(event: any): void {
    // Image loaded successfully
  }
}
